import React, { useState } from 'react';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import './EventInfoWidgetEdit.css';
import './TopWidgetEdit.css';
import { useUserCourses } from '../hooks/useCourseData';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import CourseFeaturesManagement from '../WidgetEditComponents/CourseFeaturesManagement';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';

function CourseInfoWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    const { courses, loading: coursesLoading } = useUserCourses();
    
    // Upload States
    const [isUploadingCourse, setIsUploadingCourse] = useState(false);
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);

    // Course Selection Handler
    const handleCourseSelection = (courseId) => {
        handleInputChange({ target: { name: 'courseId', value: courseId } });
    };

    // 🎓 Course Features Management - New Toggle Section
    const addFeature = () => {
        const currentFeatures = editData.customFeatures || [];
        const newFeature = {
            id: `feature${Date.now()}`,
            text: 'New course feature'
        };
        const updatedFeatures = [...currentFeatures, newFeature];
        handleInputChange({ target: { name: 'customFeatures', value: updatedFeatures } });
    };

    const removeFeature = (index) => {
        const currentFeatures = editData.customFeatures || [];
        const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'customFeatures', value: updatedFeatures } });
    };

    const updateFeature = (index, text) => {
        const currentFeatures = editData.customFeatures || [];
        const updatedFeatures = [...currentFeatures];
        updatedFeatures[index] = { ...updatedFeatures[index], text };
        handleInputChange({ target: { name: 'customFeatures', value: updatedFeatures } });
    };


    // Selected course data
    const selectedCourse = courses.find(course => course.id === editData.courseId);

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Course Information Text Configuration
    const textConfig = [
        {
            fieldName: 'modulesTitle',
            label: 'Modules Section Title',
            type: 'input',
            placeholder: 'Course Modules',
            fontField: 'modulesTitleFont',
            fontType: 'big',
            effectField: 'modulesTitleEffect',
            showToggleField: 'showModulesTitle'
        },
        {
            fieldName: 'featuresTitle',
            label: 'Features Section Title', 
            type: 'input',
            placeholder: "What's Included",
            fontField: 'featuresTitleFont',
            fontType: 'big',
            effectField: 'featuresTitleEffect',
            showToggleField: 'showFeaturesTitle'
        },
        {
            fieldName: 'durationTitle',
            label: 'Duration Label',
            type: 'input',
            placeholder: 'Total Duration',
            fontField: 'durationTitleFont',
            fontType: 'small',
            effectField: 'durationTitleEffect',
            showToggleField: 'showDurationTitle'
        },
        {
            fieldName: 'materialsTitle',
            label: 'Materials Label',
            type: 'input',
            placeholder: 'Materials',
            fontField: 'materialsTitleFont',
            fontType: 'small',
            effectField: 'materialsTitleEffect',
            showToggleField: 'showMaterialsTitle'
        },
        {
            fieldName: 'freeCourseButtonText',
            label: 'Free Course Button',
            type: 'input',
            placeholder: 'Start Free Course',
            fontField: 'freeCourseButtonFont',
            fontType: 'small',
            buttonEffectField: 'freeCourseButtonEffect',
            showToggleField: 'showFreeCourseButtonText'
        },
        {
            fieldName: 'buyCourseButtonText',
            label: 'Buy Course Button',
            type: 'input',
            placeholder: 'Enroll Now',
            fontField: 'buyCourseButtonFont',
            fontType: 'small',
            buttonEffectField: 'buyCourseButtonEffect',
            showToggleField: 'showBuyCourseButtonText'
        }
    ];

    // Color Configuration (structured like ProductInfoWidget)
    const colorConfig = [
        [
            { fieldName: 'titleColor', label: 'Title', defaultValue: '#1e293b', icon: '/img/text-fat.svg' },
            { fieldName: 'descriptionColor', label: 'Description', defaultValue: '#64748b', icon: '/img/text.svg' },
            { fieldName: 'courseTypeColor', label: 'Course Type', defaultValue: '#10b981', icon: '/img/text-fat.svg' }
        ],
        [
            { fieldName: 'durationColor', label: 'Duration', defaultValue: '#6366f1', icon: '/img/text.svg' },
            { fieldName: 'moduleColor', label: 'Modules', defaultValue: '#64748b', icon: '/img/text.svg' },
            { fieldName: 'featuresIconColor', label: 'Features Icons', defaultValue: '#10b981', icon: '/img/background.svg' }
        ],
        [
            { fieldName: 'backgroundColor', label: 'Background', defaultValue: '#ffffff', icon: '/img/background.svg' },
            { fieldName: 'buttonColor', label: 'Button', defaultValue: '#10b981', icon: '/img/background.svg' }
        ],
        [
            { fieldName: 'buttonTextColor', label: 'Button Text', defaultValue: '#ffffff', icon: '/img/text.svg' },
            { fieldName: 'heroOverlayColor', label: 'Hero Overlay', defaultValue: 'rgba(0, 0, 0, 0.4)', icon: '/img/background.svg' }
        ]
    ];

    // Font Configuration - Updated for Text Settings Section
    const fontConfig = [
        { 
            label: 'Title', 
            sizeField: 'titleSize', 
            weightField: 'titleWeight', 
            defaultSize: '32', 
            defaultWeight: '700',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        { 
            label: 'Description', 
            sizeField: 'descriptionSize', 
            weightField: 'descriptionWeight', 
            defaultSize: '16', 
            defaultWeight: '400',
            fontField: 'descriptionFont',
            fontType: 'small',
            effectField: 'descriptionEffect'
        },
        { 
            label: 'Course Type', 
            sizeField: 'courseTypeSize', 
            weightField: 'courseTypeWeight', 
            defaultSize: '14', 
            defaultWeight: '600',
            fontField: 'courseTypeFont',
            fontType: 'small'
        },
        { 
            label: 'Duration', 
            sizeField: 'durationSize', 
            weightField: 'durationWeight', 
            defaultSize: '16', 
            defaultWeight: '600',
            fontField: 'durationFont',
            fontType: 'small'
        },
        { 
            label: 'Modules', 
            sizeField: 'moduleSize', 
            weightField: 'moduleWeight', 
            defaultSize: '14', 
            defaultWeight: '500',
            fontField: 'moduleFont',
            fontType: 'small'
        }
    ];

    // 🚀 ADVANCED SETTINGS - Course Information Widget (15 Settings)
    const advancedConfig = [
        // ⬌ Dimensions (1 Setting)
        {
            fieldName: 'height',
            label: 'Widget Height',
            type: 'number',
            defaultValue: 600,
            min: 400,
            max: 1000,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },

        // ↔ Spacing (3 Settings)
        {
            fieldName: 'marginTop',
            label: 'Space Top',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'marginBottom',
            label: 'Space Bottom',
            type: 'number',
            defaultValue: 40,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'padding',
            label: 'Widget Padding',
            type: 'number',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },

        // ⚙️ Behavior (9 Toggles)
        {
            fieldName: 'showTitle',
            label: 'Show Title',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showDescription',
            label: 'Show Description',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showCourseType',
            label: 'Show Course Type',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showDuration',
            label: 'Show Duration',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showModules',
            label: 'Show Modules',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showCustomFeatures',
            label: 'Show Features',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showButton',
            label: 'Show Button',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showCourseImage',
            label: 'Show Course Image',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showFeaturePoints',
            label: 'Show Learning Outcomes',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showDocuments',
            label: 'Show Materials',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },

        // 👁 Display (2 Settings)
        {
            fieldName: 'borderRadius',
            label: 'Border Radius',
            type: 'number',
            defaultValue: 16,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'featuresLayout',
            label: 'Features Layout',
            type: 'select',
            defaultValue: 'grid',
            options: [
                { value: 'grid', label: 'Grid Layout (Multiple Columns)' },
                { value: 'column', label: 'Single Column (All Stacked)' }
            ],
            icon: '/img/element.svg'
        },
        {
            fieldName: 'moduleStyle',
            label: 'Module Style',
            type: 'select',
            defaultValue: 'apple',
            options: [
                { value: 'apple', label: '🍎 Apple Style - Clean & Minimal' },
                { value: 'gradient', label: '🌈 Gradient - Colorful & Modern' },
                { value: 'neon', label: '⚡ Neon - Futuristic & Bold' },
                { value: 'warm', label: '🔥 Warm - Cozy & Inviting' },
                { value: 'minimal', label: '📝 Minimal - Simple & Clean' },
                { value: 'glass', label: '💎 Glass - Transparent & Elegant' }
            ],
            icon: '/img/element.svg'
        }
    ];

    // Layout Configuration - Leer nach Advanced Settings Migration
    const layoutConfig = [];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Course Selection */}
                <div className="widget-trenner">
                    <h3 style={{ marginBottom: '10px' }}>Course Connection</h3>
                </div>

                <div className="input-group">
                    <select 
                        name="courseId" 
                        value={editData.courseId || ''} 
                        onChange={(e) => handleCourseSelection(e.target.value)}
                        className="widgetInputField"
                    >
                        <option value="">Select Course...</option>
                        {coursesLoading ? (
                            <option disabled>Loading courses...</option>
                        ) : courses.length === 0 ? (
                            <option disabled>No courses found - Create a course in Sales Platform first</option>
                        ) : (
                            courses.map(course => (
                                <option key={course.id} value={course.id}>
                                    🎓 {course.title} ({course.status})
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {/* Selected Course Info */}
                {selectedCourse && (
                    <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#d1fae5',
                        borderRadius: '8px',
                        border: '1px solid #10b981'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            marginBottom: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#065f46'
                        }}>
                            <i className="fas fa-graduation-cap" style={{ fontSize: '12px' }}></i>
                            Course Connected: {selectedCourse.title}
                        </div>
                        <div style={{ fontSize: '12px', color: '#047857', lineHeight: '1.4' }}>
                            Type: {selectedCourse.subtype || 'Course'} • 
                            Modules: {selectedCourse.contentData?.videoModules?.length || 0} • 
                            Status: {selectedCourse.status}
                        </div>
                    </div>
                )}


                {/* 🎓 COURSE IMAGE CONTAINER - New Image System */}
                <ImageContainer
                    label="Course Image"
                    fieldName="courseImage"
                    currentImage={editData.courseImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingCourse}
                    setIsUploading={setIsUploadingCourse}
                    showToggle={true}
                    toggleLabel="Show Course Image"
                    toggleFieldName="showCourseImage"
                    toggleChecked={editData.showCourseImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Width',
                            fieldName: 'courseImageWidth',
                            defaultValue: 300,
                            min: 200,
                            max: 500,
                            step: 10,
                            unit: 'px',
                            description: 'Adjust course image width'
                        },
                        {
                            type: 'size',
                            label: 'Image Height',
                            fieldName: 'courseImageHeight',
                            defaultValue: 200,
                            min: 150,
                            max: 400,
                            step: 10,
                            unit: 'px',
                            description: 'Adjust course image height'
                        },
                        {
                            type: 'size',
                            label: 'Border Radius',
                            fieldName: 'courseImageBorderRadius',
                            defaultValue: 12,
                            min: 0,
                            max: 30,
                            step: 2,
                            unit: 'px',
                            description: 'Course image corner roundness'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 🖼️ BACKGROUND IMAGE CONTAINER - New Image System with Effects */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Background Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 85,
                            min: 10,
                            max: 100,
                            step: 5,
                            unit: '%',
                            description: 'Background image transparency',
                            convertValue: true,
                            color: '#8b5cf6',
                            icon: '/img/background.svg'
                        },
                        {
                            type: 'image-effect',
                            label: 'Image Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />



                {/* 🚀 REVOLUTIONARY TEXT SETTINGS - Course Information Widget */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                />

                {/* 🚀 ADVANCED SETTINGS - Course Information Widget */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                />

                {/* 🎓 COURSE FEATURES MANAGEMENT - New Toggle Section */}
                {editData.showCustomFeatures && (
                    <CourseFeaturesManagement 
                        features={editData.customFeatures || []}
                        addFeature={addFeature}
                        removeFeature={removeFeature}
                        updateFeature={updateFeature}
                        editData={editData}
                    />
                )}

                {/* Stripe Integration Section */}
                <div className="widget-trenner" style={{ marginTop: '30px' }}>
                    <h3>💳 Payment Integration</h3>
                </div>

                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '15px'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                    }}>
                        <img 
                            src="/img/stripe.svg" 
                            alt="Stripe" 
                            style={{ width: '24px', height: '24px' }} 
                        />
                        <div>
                            <div style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                                Stripe Checkout
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                Secure payment processing for paid courses
                            </div>
                        </div>
                    </div>
                    <ToggleSwitch
                        label=""
                        fieldName="useStripeCheckout"
                        checked={editData.useStripeCheckout !== false}
                        onChange={handleInputChange}
                    />
                </div>

                {editData.useStripeCheckout !== false && (
                    <div style={{
                        padding: '16px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        border: '1px solid #0ea5e9',
                        marginBottom: '15px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <i className="fas fa-info-circle" style={{ color: '#0ea5e9' }}></i>
                            <span style={{ fontWeight: '600', color: '#0c4a6e', fontSize: '13px' }}>
                                Stripe Configuration Required
                            </span>
                        </div>
                        <p style={{ 
                            fontSize: '12px', 
                            color: '#0c4a6e', 
                            margin: '0',
                            lineHeight: '1.4'
                        }}>
                            For paid courses, configure Stripe Product ID and Price ID in your course settings. 
                            Free courses will automatically show a contact form instead of payment.
                        </p>
                    </div>
                )}

                <div className="input-group">
                    <label>Custom Button Link</label>
                    <input
                        type="url"
                        name="buttonLink"
                        value={editData.buttonLink || ''}
                        onChange={handleInputChange}
                        placeholder="https://your-custom-link.com (overrides Stripe)"
                        className="widgetInputField"
                    />
                    <small style={{ color: '#6b7280', fontSize: '11px' }}>
                        Optional: Custom link overrides Stripe checkout
                    </small>
                </div>


                {/* Use MoreSettingsSection like ProductInfoWidget */}
                <MoreSettingsSection
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={layoutConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                    showImageEffects={editData.backgroundImage ? true : false}
                    showLayoutOptions={true}
                    showResponsiveOptions={true}
                />
            </div>
        </div>
    );
}

export default CourseInfoWidgetEdit;
